import { AddIcon, EmptyCaseImage } from '@approvalmax/ui';
import { Button, Form, Grid, Popup, Radio, Spacing, Text } from '@approvalmax/ui/src/components';
import { FC, memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { routingService } from 'services/routing';
import { AmaxPayOpenBankingBankAccount } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';
import { getPath, Path } from 'urlBuilder';

import { BankAccountCard } from './components/BankAccountCard/BankAccountCard';
import { fieldNameBankAccountId } from './SelectBankAccountPopupContent.constants';
import { bankAccountComparator } from './SelectBankAccountPopupContent.helpers';
import { Loading } from './SelectBankAccountPopupContent.loading';
import { messages } from './SelectBankAccountPopupContent.messages';
import { FieldValues, SelectBankAccountPopupContentProps } from './SelectBankAccountPopupContent.types';

export const SelectBankAccountPopupContent: FC<SelectBankAccountPopupContentProps> = memo((props) => {
    const { onSelectBankAccount, progress, bankAccounts, companyCurrency, selectedBankAccountId } = props;

    const company = useCompanyBasedOnUrl();
    const form = useForm<FieldValues>({ values: { bankAccountId: selectedBankAccountId } });
    const formSelectedBankAccountId = form.watch('bankAccountId');

    const onSubmit = form.handleSubmit(({ bankAccountId }) => {
        if (bankAccountId) {
            onSelectBankAccount(bankAccountId);
        }
    });

    const { matchingCurrencyBankAccounts, differentCurrencyBankAccounts } = useMemo(() => {
        const matchingCurrencyBankAccounts: AmaxPayOpenBankingBankAccount[] = [];
        const differentCurrencyBankAccounts: AmaxPayOpenBankingBankAccount[] = [];

        bankAccounts.forEach((bankAccount) => {
            if (bankAccount.currencyCode === companyCurrency) {
                matchingCurrencyBankAccounts.push(bankAccount);
            } else {
                differentCurrencyBankAccounts.push(bankAccount);
            }
        });

        matchingCurrencyBankAccounts.sort(bankAccountComparator);

        return {
            matchingCurrencyBankAccounts,
            differentCurrencyBankAccounts,
        };
    }, [bankAccounts, companyCurrency]);

    const goToBankAccounts = useCallback(
        () => routingService.push(getPath(Path.bankAccounts, company.id)),
        [company.id]
    );

    if (progress) {
        return <Loading />;
    }

    if (!bankAccounts.length) {
        return (
            <Popup.Body>
                <Grid justifyItems='center' gap='24px'>
                    <EmptyCaseImage />

                    <Text font='headline' fontSize='small' fontWeight='medium'>
                        {messages.noBankAccounts}
                    </Text>

                    <Text font='body' fontSize='medium' fontWeight='regular'>
                        {messages.connect}
                    </Text>

                    <Button size='large' color='blue80' startIcon={<AddIcon />} onClick={goToBankAccounts}>
                        {messages.addBankAccount}
                    </Button>
                </Grid>
            </Popup.Body>
        );
    }

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button size='medium' color='blue80' type='submit'>
                        {messages.save}
                    </Button>
                }
            />

            <Popup.Body spacing='32'>
                <Radio.Group required name={fieldNameBankAccountId}>
                    <Grid gap='24px' width='100%'>
                        <div>
                            {matchingCurrencyBankAccounts.map((bankAccount) => (
                                <BankAccountCard
                                    key={bankAccount.id}
                                    bankAccount={bankAccount}
                                    selected={bankAccount.id === formSelectedBankAccountId}
                                />
                            ))}
                        </div>

                        {differentCurrencyBankAccounts.length && (
                            <div>
                                <Text font='label' fontSize='small' color='midnight80'>
                                    {messages.otherCurrencies}
                                </Text>

                                <Spacing height={8} />

                                {differentCurrencyBankAccounts.map((bankAccount) => (
                                    <BankAccountCard
                                        key={bankAccount.id}
                                        bankAccount={bankAccount}
                                        hasCurrencyMismatch
                                    />
                                ))}
                            </div>
                        )}
                    </Grid>
                </Radio.Group>
            </Popup.Body>
        </Form>
    );
});

SelectBankAccountPopupContent.displayName = 'SelectBankAccountPopupContent';
